import { useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { pusherInstance } from '../index'

// Subscribe to `notification.created` on the authenticated user's
// private Pusher channel and invalidate every cached notification
// slice so the bell badge bumps immediately and the inbox dropdown
// refetches when next opened.
//
// This is the silent-UX option — no toast, no browser popup. The
// bell badge in Header and the dropdown both read from
// `['notifications', 'inbox']` (via `useInboxCounts`), so a single
// invalidate by prefix catches every related slice (`inbox`,
// `list`, `details`, …) without enumerating each shape.
//
// Event payload is intentionally ignored: the worker service
// (`mdg-guardian-worker`) might emit the full row or just
// `{ notificationUuid }`, and a refetch always re-derives canonical
// state from the server. Inserting the payload directly into the
// cache would risk drift if the worker's shape doesn't match
// `GET /notifications` exactly.
//
// Signin race: NotificationDropdown mounts at the same time as
// `pusherInstance()` is first called from Header, so `pusher.user`
// can be `null` for the first few hundred ms while the auth
// handshake is in flight. A single `pusher.user?.bind` would no-op
// in that window and miss the first real event, so the effect
// retries every 250ms (capped at ~5s) until `pusher.user` is ready,
// matching the existing Reports.jsx pattern once signin completes.
export default function useNotificationStream() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const pusher = pusherInstance()
    if (!pusher) return undefined

    let bound = false
    const handler = () => {
      // Drop every cached notification slice so the bell badge bumps
      // on the next render and the dropdown refetches when opened.
      // `refetchType: 'active'` (the default) ensures the
      // always-active bell query refetches immediately rather than
      // waiting for the 30s staleTime to expire.
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }

    const tryBind = () => {
      if (bound || !pusher.user) return
      pusher.user.bind('notification.created', handler)
      bound = true
    }

    // Signin may have already completed (e.g. when this hook mounts
    // on a later navigation). Bind synchronously when possible.
    tryBind()
    if (bound) {
      return () => pusher.user?.unbind('notification.created', handler)
    }

    // Otherwise poll briefly until `pusher.user` resolves. 20
    // attempts × 250ms ≈ 5s — generous for a handshake that
    // typically completes in well under a second.
    let attempts = 0
    const MAX_ATTEMPTS = 20
    const intervalId = setInterval(() => {
      attempts += 1
      tryBind()
      if (bound || attempts >= MAX_ATTEMPTS) {
        clearInterval(intervalId)
        if (!bound) {
          // Surface the failure rather than silently dropping events.
          // The console message gives the operator enough to check
          // VITE_APP_WORKER_API_URL / VITE_PUSHER_API_KEY / network.
          console.warn(
            '[notifications] pusher user channel never became ready; ' +
              'notification.created events will be missed until reload.'
          )
        }
      }
    }, 250)

    return () => {
      clearInterval(intervalId)
      if (bound) {
        pusher.user?.unbind('notification.created', handler)
      }
    }
  }, [queryClient])
}
